import { v4 as uuid } from 'uuid';
import { OrderItem, OrderItemAddon, OrderItemOption, OrderItemSlot, OrderItemSlotItem } from "./OrderItem.js";
import { DeliveryZoneCountryZipCodeRule } from "./DeliveryZone.js";
import { ProductType } from "./Product.js";
import { CartBreakdown } from "./CartBreakdown.js";
import { OrderRequest } from "./OrderRequest.js";
import { ErrorCode } from "./ErrorCode.js";
import { DeliveryType, PaymentType } from "./Enums.js";
import { TodoItem } from "./TodoItem.js";
export { PaymentType } from "./Enums.js";
export class OrderCalculator {
    company;
    orderItems = [];
    orderRequest;
    customer = null;
    constructor(company, orderRequest, customer) {
        this.company = company;
        this.orderRequest = orderRequest;
        if (customer) {
            this.customer = customer;
        }
        if (this.orderRequest.cartItems && this.orderRequest.cartItems.length > 0) {
            for (let cartItem of this.orderRequest.cartItems) {
                let orderItem = this.convertCartItem(cartItem);
                if (orderItem) {
                    this.orderItems.push(orderItem);
                }
            }
        }
    }
    validate() {
        if (!this.company.settings) {
            console.error('@OrderCalculator#validate: Called without settings');
            return {};
        }
        let errors = {};
        if (!this.orderRequest.deliveryType) {
            errors['deliveryType'] = { code: ErrorCode.missing };
        }
        if (this.orderRequest.deliveryType && !Object.keys(DeliveryType).includes(this.orderRequest.deliveryType)) {
            errors['deliveryType'] = { code: ErrorCode.invalidValue };
        }
        if (!this.orderRequest.zoneId) {
            errors['zoneId'] = { code: ErrorCode.missing };
        }
        if (!this.orderRequest.pointofsaleId) {
            errors['zoneId'] = { code: ErrorCode.missing };
        }
        let zone = this.company.getSalesZoneById(this.orderRequest.zoneId);
        if (!zone) {
            errors['zoneId'] = { code: ErrorCode.invalidValue };
        }
        else {
            let pointofsale = zone.getPointOfSale(this.orderRequest.pointofsaleId);
            if (!pointofsale) {
                errors['pointofsaleId'] = { code: ErrorCode.invalidValue };
            }
        }
        if (zone && zone.collectBillingAddress) {
            if (!this.orderRequest.billingAddress) {
                errors['billingAddress'] = { code: ErrorCode.missing };
            }
            if (this.orderRequest.billingAddress && !this.orderRequest.billingAddress.isComplete()) {
                errors['billingAddress'] = { code: ErrorCode.incompleteValue };
            }
        }
        if (this.orderRequest.deliveryType == DeliveryType.delivery) {
            //if the zone collects billing addresses we assume its just incomplete for now and don't raise an additional error
            let hasBillingAddress = (zone && zone.collectBillingAddress) || this.orderRequest.billingAddress && this.orderRequest.billingAddress.isComplete();
            if (!hasBillingAddress && (!this.orderRequest.deliveryAddress || !this.orderRequest.deliveryAddress.isComplete())) {
                errors['deliveryAddress'] = { code: ErrorCode.incompleteValue };
            }
        }
        if (!this.orderRequest.paymentType) {
            errors['paymentType'] = { code: ErrorCode.missing };
        }
        if (this.orderRequest.paymentType == PaymentType.offline && !this.company.settings.enableOfflinePayments) {
            errors['paymentType'] = { code: ErrorCode.invalidValue };
        }
        const isBackendOrder = Boolean(this.orderRequest.userId);
        let companyIsOpen = Boolean(this.company.settings.isOpen);
        let salesZoneIsOpen = Boolean(zone && !zone.hidden && !zone.closed);
        //only check if the company is open if its a request from the website and not from the backend
        // its an error if any of the two is closed. 
        if (!isBackendOrder && (!salesZoneIsOpen || !companyIsOpen)) {
            if (!companyIsOpen) {
                errors['closed'] = { code: ErrorCode.companyIsClosed };
            }
            if (!salesZoneIsOpen) {
                errors['closed'] = { code: ErrorCode.salesZoneIsClosed };
            }
        }
        //only check POS payment method if it is no backend order 
        if (!isBackendOrder && zone) {
            if (this.orderRequest.paymentType == PaymentType.offline && !zone.enableOfflinePayment) {
                console.error('zone doesnt allow offlinepayment');
                errors['paymentType'] = { code: ErrorCode.invalidValue };
            }
            if (this.orderRequest.paymentType == PaymentType.online && !zone.enableOnlinePayment) {
                errors['paymentType'] = { code: ErrorCode.invalidValue };
            }
        }
        // //check if it has an address if it's above the simplifiedInvoiceTreshold
        // let cartBreakDown = this.getCartBreakDown()
        // let simplifiedInvoiceTreshold = 400
        // if(cartBreakDown && cartBreakDown.total > simplifiedInvoiceTreshold && (!this.orderRequest.billingAddress || !this.orderRequest.billingAddress.isComplete()) ){
        //     console.log('Billing address needed bacause of simplificada Limit')
        //     errors['billingAddress'] = { code: ErrorCode.missing }
        // }
        return Object.keys(errors).length > 0 ? errors : null;
    }
    getCombinedWeight() {
        let weight = 0;
        // console.log('@Cart#getWeight:this.orderItems', this.orderItems)
        for (let item of this.orderItems) {
            if (!item || !item.quantity) {
                console.error('@Cart#getWeight: Item not found or no quantity', item);
                continue;
            }
            if (!item.productId) {
                console.error('@Cart#getWeight: Item without productId', item);
                continue;
            }
            let product = this.company.getProductById(item.productId);
            if (!product) {
                console.error('@Cart#getWeight: product not found', item);
                continue;
            }
            if (!product.weight) {
                continue;
            }
            let productWeight = Number.parseFloat('' + product.weight);
            let quantity = Number.parseInt('' + item.quantity);
            weight += productWeight * quantity;
        }
        return weight;
    }
    getApplicableDeliveryZone() {
        let cartWeight = this.getCombinedWeight();
        if (this.orderRequest.deliveryType != DeliveryType.delivery) {
            return null;
        }
        if (!this.company) {
            console.error('@Cart#getApplicableDeliveryZone: No company found', this.company);
            return null;
        }
        if (!this.company.settings) {
            console.error('@Cart#getApplicableDeliveryZone: No settings found', this.company);
            return null;
        }
        if (!this.company.settings.deliveryZones) {
            console.error('@Cart#getApplicableDeliveryZone: No  deliveryzones found', this.company);
            return null;
        }
        let addressToUse = this.orderRequest.deliveryAddress ? this.orderRequest.deliveryAddress : this.orderRequest.billingAddress;
        if (!addressToUse) {
            console.error('@Cart#getApplicableDeliveryZone: No deliveryAddress found');
            return null;
        }
        if (!addressToUse.countryCode) {
            console.error('applicableDeliveryZone: No country code found', addressToUse);
            return null;
        }
        if (!addressToUse.zip) {
            console.error('applicableDeliveryZone: No zip found', addressToUse);
            return null;
        }
        let applicableZonesForCountryAndZip = [];
        //find all zones which match the country and zip settings
        for (let zone of this.company.settings.deliveryZones) {
            if (zone.countries) {
                for (let countryRule of zone.countries) {
                    if (countryRule.code == addressToUse.countryCode) {
                        if (countryRule.validInZipCodesRule == DeliveryZoneCountryZipCodeRule.all) {
                            applicableZonesForCountryAndZip.push(zone);
                            continue;
                        }
                        let cleanupZip = (str) => {
                            return str.trim().replaceAll(' ', '');
                        };
                        let cleanedUpZips = [];
                        if (countryRule.validInZipCodesConfig) {
                            cleanedUpZips = ('' + countryRule.validInZipCodesConfig).split(',').map(cleanupZip);
                        }
                        addressToUse.zip = cleanupZip(addressToUse.zip);
                        let configIncludesZip = false;
                        for (let zipTemplate of cleanedUpZips) {
                            //definitly right
                            if (zipTemplate == addressToUse.zip) {
                                configIncludesZip = true;
                                break;
                            }
                            //definitly wrong
                            if (zipTemplate.length != addressToUse.zip.length) {
                                continue;
                            }
                            let patternMatched = true;
                            for (let i = 0; i < zipTemplate.length; i++) {
                                let zipCharAtThisPos = addressToUse.zip.charAt(i);
                                let templateCharAtThisPos = zipTemplate.charAt(i);
                                if (zipCharAtThisPos != templateCharAtThisPos && templateCharAtThisPos != '*') {
                                    patternMatched = false;
                                    break;
                                }
                            }
                            if (patternMatched) {
                                configIncludesZip = true;
                                break;
                            }
                        }
                        if (countryRule.validInZipCodesRule == 'only' && configIncludesZip) {
                            applicableZonesForCountryAndZip.push(zone);
                        }
                        if (countryRule.validInZipCodesRule == 'except' && !configIncludesZip) {
                            applicableZonesForCountryAndZip.push(zone);
                        }
                    }
                }
            }
        }
        //if we found some select the cheapest
        let selectedZone = null;
        let cheapestPrice = null;
        for (let zone of applicableZonesForCountryAndZip) {
            let priceOfZone = null;
            let lowestWeightWhichCoversTheCart = null;
            if (zone.prices) {
                for (let priceBracket of zone.prices) {
                    if ((priceBracket.price == null || priceBracket.price == undefined || isNaN(Number.parseFloat('' + priceBracket.price)))) {
                        continue;
                    }
                    //if no maxweight is given treat it as a very high number
                    //so price brackets with a set max weight have a chance to be chosen
                    let maxWeight = priceBracket.maxWeight ? Number.parseFloat('' + priceBracket.maxWeight) : Number.MAX_SAFE_INTEGER;
                    if ((maxWeight >= cartWeight) && (lowestWeightWhichCoversTheCart == null ||
                        maxWeight < lowestWeightWhichCoversTheCart)) {
                        lowestWeightWhichCoversTheCart = priceBracket.maxWeight;
                        priceOfZone = Number.parseFloat('' + priceBracket.price);
                    }
                }
            }
            //its the first zone
            if (selectedZone == null) {
                selectedZone = zone;
                cheapestPrice = priceOfZone;
                continue;
            }
            if ((priceOfZone || 0) < (cheapestPrice || 0)) {
                cheapestPrice = priceOfZone;
                selectedZone = zone;
            }
        }
        return selectedZone ? { zone: selectedZone, price: cheapestPrice, weight: cartWeight } : null;
    }
    /**
     * takes a cart item retrieves every bit of information neeeded and saves everything into an orderitem
     * which is very similar but includes taxrates, prices, names,...
     * so the order item can be displayed correctly (as at the time of order) even if the shops configuration has changed over time
     * @param cartItem
     * @returns an OrderItem with all required information set
     */
    convertCartItem(cartItem) {
        let orderItem = new OrderItem(cartItem.id);
        if (!cartItem || !cartItem.quantity) {
            console.error('Item not found or no quantity', cartItem);
            return null;
        }
        if (!cartItem.productId) {
            console.error('@Cart#convertCartItem: no productId', cartItem);
            return null;
        }
        orderItem.quantity = cartItem.quantity;
        orderItem.productId = cartItem.productId;
        orderItem.courseId = cartItem.courseId;
        orderItem.note = cartItem.note;
        let product = this.company.getProductById(cartItem.productId);
        if (!product) {
            console.error('@Cart#convertCartItem: Product not found', cartItem.productId, cartItem);
            return null;
        }
        if (!product.price) {
            console.error('@Cart#convertCartItem: no price', cartItem, product);
            return null;
        }
        if (!this.company.settings) {
            console.error('@Cart#convertCartItem: No settings found', this.company);
            return null;
        }
        if (!this.company.settings.vatRates) {
            console.error('@Cart#convertCartItem: No vatRates found', this.company.settings);
            return null;
        }
        orderItem.name = product.name;
        orderItem.basePrice = product.price;
        let combinedPrice = product.price;
        orderItem.serviceInformation = cartItem.serviceInformation;
        //get the correct vat rate for that cart item
        //thats the default vat rate
        orderItem.vatRate = this.company.settings.vat ? Number.parseFloat('' + this.company.settings.vat) : 0;
        //if the product has a custom vat rate set and we find it apply that            
        if (product.vatRate) {
            // console.log('settings', this.company.settings)
            let customVatRate = this.company.settings.getVatRateById(product.vatRate);
            if (customVatRate) {
                orderItem.vatRate = Number.parseFloat('' + customVatRate.rate);
            }
        }
        if (cartItem.addons && Object.values(cartItem.addons).length > 0) {
            //compatibility if addons is a map convert it to an array of ids
            if (!Array.isArray(cartItem.addons)) {
                //console.error('NOW IT HAPPENED, THE LONG LOST MAP HAS ARRIVED',this.orderRequest,cartItem.addons)
                cartItem.addons = Object.keys(cartItem.addons);
            }
            for (let addonId of cartItem.addons) {
                let addon = product.getAddonById(addonId);
                if (!addon) {
                    continue;
                }
                let price = addon.price && Number.parseFloat('' + addon.price) ? Number.parseFloat('' + addon.price) : 0;
                combinedPrice += price;
                orderItem.addons.push(new OrderItemAddon(addon.id, {
                    name: addon.name,
                    price: price
                }));
            }
        }
        //convert all options
        if (cartItem.options && Object.values(cartItem.options).length > 0) {
            for (let optionId in cartItem.options) {
                let option = product.getOptionById(optionId);
                if (!option) {
                    continue;
                }
                let valueId = cartItem.options[optionId];
                if (!valueId) {
                    continue;
                }
                let value = option.getValueById(valueId);
                if (!value) {
                    continue;
                }
                let correction = value.correction && Number.parseFloat('' + value.correction) ? Number.parseFloat('' + value.correction) : 0;
                combinedPrice += correction;
                orderItem.options.push(new OrderItemOption(option.id, {
                    id: option.id,
                    name: option.name ? option.name : null,
                    value: {
                        id: value.id,
                        name: value.name,
                        correction: correction
                    }
                }));
            }
        }
        if (product.type == ProductType.combined && product.slots && product.slots.length > 0 && cartItem.chosen) {
            for (let slot of product.slots) {
                let slotItemId = cartItem.chosen[slot.id] ? cartItem.chosen[slot.id] : null;
                if (!slotItemId) {
                    console.error("@Cart#convertCartItem: no product selected for slot", slot.id);
                    continue;
                }
                let chosenSlotItem = slot.getSlotItemById(slotItemId);
                if (!chosenSlotItem) {
                    console.error("@Cart#convertCartItem: Slot doesn't include slotitem with id", slot.id, slotItemId);
                    continue;
                }
                if (chosenSlotItem.hidden) {
                    console.error("@Cart#convertCartItem: SlotItem is hidden", slot.id, slotItemId);
                    continue;
                }
                let productOfLine = this.company.getProductById(chosenSlotItem.productId);
                if (!productOfLine) {
                    console.error("@Cart#convertCartItem: Selected Product not found", slot.id, slotItemId);
                    continue;
                }
                let correction = chosenSlotItem.correction && Number.parseFloat('' + chosenSlotItem.correction) ? Number.parseFloat('' + chosenSlotItem.correction) : 0;
                combinedPrice += correction;
                orderItem.chosen[slot.id] = new OrderItemSlot(slot.id, slot.name, new OrderItemSlotItem(productOfLine.name, slotItemId, correction));
            }
        }
        orderItem.price = combinedPrice;
        return orderItem;
    }
    getCartBreakDown() {
        if (!this.company.products) {
            console.error('@Cart#getCartBreakDown: No products found', this.company);
            return null;
        }
        if (!this.company.settings || !this.company.settings.vatRates) {
            console.error('@Cart#getCartBreakDown: No settings or vatRates found', this.company.settings);
            return null;
        }
        let cartBreakDown = new CartBreakdown();
        for (let item of this.orderItems) {
            let product = this.company.getProductById(item.productId);
            if (!product) {
                continue;
            }
            let infos = [];
            let productPriceWithAddonsAndCorrections = product.price;
            for (let addon of item.addons) {
                productPriceWithAddonsAndCorrections += addon.price;
                infos.push(addon.name);
            }
            for (let option of item.options) {
                if (option.value) {
                    infos.push(option.value.name);
                    if (option.value.correction) {
                        productPriceWithAddonsAndCorrections += option.value.correction;
                    }
                }
            }
            if (product.type == ProductType.combined && product.slots && product.slots.length > 0 && item.chosen) {
                for (let slot of product.slots) {
                    let orderItemSlot = item.chosen[slot.id];
                    if (!orderItemSlot) {
                        continue; //TODO make sure its only an empty slot
                    }
                    let chosenSlotItem = orderItemSlot.chosen;
                    infos.push(chosenSlotItem.name);
                    if (chosenSlotItem && chosenSlotItem.correction) {
                        productPriceWithAddonsAndCorrections += Number.parseFloat("" + chosenSlotItem.correction);
                    }
                }
            }
            let subtotal = item.quantity * productPriceWithAddonsAndCorrections;
            cartBreakDown.grossAmount += subtotal;
            let net = (subtotal / (item.vatRate + 100)) * 100;
            let vat = subtotal - net;
            cartBreakDown.netAmount += net;
            let vatRateKey = Number.parseFloat('' + item.vatRate).toFixed(2);
            if (!cartBreakDown.vatByRate[vatRateKey]) {
                cartBreakDown.vatByRate[vatRateKey] = 0;
            }
            cartBreakDown.vatByRate[vatRateKey] += vat;
            cartBreakDown.items[item.id] = {
                productId: product.id,
                quantity: item.quantity,
                net: net,
                price: productPriceWithAddonsAndCorrections,
                subtotal: subtotal,
                vatRate: item.vatRate,
                vat: vat,
                name: product.name,
                infos: infos
            };
        }
        let allDiscountIds = [];
        //add zone-based discounts
        let zone = this.company.getSalesZoneById(this.orderRequest.zoneId);
        if (zone && zone.autoAppliedDiscounts && zone.autoAppliedDiscounts.length > 0) {
            allDiscountIds = allDiscountIds.concat(zone.autoAppliedDiscounts);
        }
        //add customer-based discounts
        if (this.customer && this.customer.autoAppliedDiscounts && this.customer.autoAppliedDiscounts.length > 0) {
            allDiscountIds = allDiscountIds.concat(this.customer.autoAppliedDiscounts);
        }
        //the discounts which came with the orderrequest (set by backend user, or by voucher Code)
        if (this.orderRequest.discounts && this.orderRequest.discounts.length > 0) {
            allDiscountIds = allDiscountIds.concat(this.orderRequest.discounts);
        }
        if (allDiscountIds.length > 0) {
            //remove duplicate dicountIDs
            allDiscountIds = [...new Set(allDiscountIds)];
            for (let discountId of allDiscountIds) {
                let discount = this.company.getDiscountById(discountId);
                if (!discount) {
                    console.log('add discounts: discount not found', discountId);
                    continue;
                }
                let percentage = Number.parseFloat('' + discount.percentage);
                let discountAmount = cartBreakDown.grossAmount / 100 * percentage;
                cartBreakDown.grossAmount = cartBreakDown.grossAmount - discountAmount;
                cartBreakDown.netAmount = cartBreakDown.netAmount - cartBreakDown.netAmount / 100 * percentage;
                for (let vatRate in cartBreakDown.vatByRate) {
                    cartBreakDown.vatByRate[vatRate] = cartBreakDown.vatByRate[vatRate] / 100 * (100 - percentage);
                }
                let info = {
                    name: discount.name,
                    percentage: discount.percentage,
                    amount: discountAmount,
                    id: discount.id
                };
                cartBreakDown.appliedDiscounts.push(info);
            }
        }
        cartBreakDown.subtotal = cartBreakDown.total = cartBreakDown.grossAmount;
        let deliveryZoneInfo = this.getApplicableDeliveryZone();
        cartBreakDown.weight = deliveryZoneInfo && deliveryZoneInfo.weight || null;
        cartBreakDown.deliveryZoneId = deliveryZoneInfo && deliveryZoneInfo.zone && deliveryZoneInfo.zone.id || null;
        if (this.orderRequest.deliveryType == DeliveryType.delivery && deliveryZoneInfo && deliveryZoneInfo.price) {
            cartBreakDown.deliveryCosts = deliveryZoneInfo.price;
            let vatRateForDelivery = Number.parseFloat('' + this.company.settings.vat);
            let vatForDelivery = (cartBreakDown.deliveryCosts / (100 + vatRateForDelivery)) * vatRateForDelivery;
            cartBreakDown.netAmount += (cartBreakDown.deliveryCosts - vatForDelivery);
            cartBreakDown.grossAmount += cartBreakDown.deliveryCosts;
            cartBreakDown.total = cartBreakDown.grossAmount;
            let vatRateKey = Number.parseFloat('' + vatRateForDelivery).toFixed(2);
            if (!cartBreakDown.vatByRate[vatRateKey]) {
                cartBreakDown.vatByRate[vatRateKey] = 0;
            }
            cartBreakDown.vatByRate[vatRateKey] += vatForDelivery;
        }
        //has to be at the very end because the total logic depends on it
        if (this.company.settings.tipIsEnabled && this.orderRequest.tipPercentage && !isNaN(this.orderRequest.tipPercentage) && this.orderRequest.tipPercentage > 0) {
            cartBreakDown.tip = (cartBreakDown.subtotal / 100 * this.orderRequest.tipPercentage);
            if (this.company.settings.tipIsTaxed && this.company.settings.vatForTips) {
                cartBreakDown.tipIsTaxed = true;
                let vatRateForTip = Number.parseFloat('' + this.company.settings.vatForTips);
                cartBreakDown.grossAmount += cartBreakDown.tip;
                cartBreakDown.total = cartBreakDown.grossAmount;
                let tipWithoutVat = cartBreakDown.tip / (100 + vatRateForTip) * 100;
                let vatOfTip = cartBreakDown.tip - tipWithoutVat;
                cartBreakDown.netAmount += tipWithoutVat;
                let vatRateKey = Number.parseFloat('' + vatRateForTip).toFixed(2);
                if (!cartBreakDown.vatByRate[vatRateKey]) {
                    cartBreakDown.vatByRate[vatRateKey] = 0;
                }
                cartBreakDown.vatByRate[vatRateKey] += vatOfTip;
            }
            else {
                cartBreakDown.total = cartBreakDown.grossAmount + cartBreakDown.tip;
            }
        }
        cartBreakDown.vat = cartBreakDown.grossAmount - cartBreakDown.netAmount;
        return cartBreakDown;
    }
    getTodoItems() {
        if (!this.orderItems) {
            return [];
        }
        let todoItems = [];
        for (let item of this.orderItems) {
            let product = this.company.getProductById(item.productId);
            if (!product || !product.id) {
                continue;
            }
            let addonsInfo = [];
            for (let addon of item.addons) {
                // console.log('addonsInfo', addonsInfo, addon)
                addonsInfo.push({ id: addon.id, name: addon.name });
            }
            let optionsInfo = [];
            for (let option of item.options) {
                if (option.value) {
                    optionsInfo.push({ id: option.id, name: option.name, value: { id: option.value.id, name: option.value.name } });
                }
            }
            //if its a combined Product create todo items for every chosen product            
            if (product.type == ProductType.combined && product.slots && product.slots.length > 0 && item.chosen) {
                for (let slot of product.slots) {
                    let orderItemSlot = item.chosen[slot.id];
                    if (!orderItemSlot) {
                        continue;
                    }
                    let slotItem = orderItemSlot.chosen;
                    if (!slotItem) {
                        continue;
                    }
                    let slotProduct = this.company.getProductById(slotItem.productId);
                    if (!slotProduct) {
                        continue;
                    }
                    for (let i = 0; i < item.quantity; i++) {
                        let key = item.productId + '-' + slotProduct.id;
                        let customerSuppliedInfo = item.serviceInformation && item.serviceInformation[key] && item.serviceInformation[key].length > i ? item.serviceInformation[key][i] : null;
                        let todoItemId = item.id + '-' + slot.id + '-' + i;
                        let todoItem = new TodoItem(todoItemId, {
                            productId: slotProduct.id,
                            parentProductId: product.id,
                            name: slotProduct.name,
                            parentName: product.name,
                            orderItemId: item.id,
                            addons: addonsInfo,
                            options: optionsInfo,
                            note: customerSuppliedInfo && customerSuppliedInfo.note ? customerSuppliedInfo.note : (item.note ? item.note : null),
                            courseId: customerSuppliedInfo && customerSuppliedInfo.courseId ? customerSuppliedInfo.courseId : slotProduct.defaultCourseId,
                            locationId: slotProduct.locationId ? slotProduct.locationId : null,
                        });
                        todoItems.push(todoItem);
                    }
                }
            }
            else {
                //its a regular product
                for (let i = 0; i < item.quantity; i++) {
                    let key = product.id;
                    let customerSuppliedInfo = item.serviceInformation && item.serviceInformation[key] && item.serviceInformation[key].length > i ? item.serviceInformation[key][i] : null;
                    let todoItemId = item.id + '-' + i;
                    let todoItem = new TodoItem(todoItemId, {
                        productId: product.id,
                        parentProductId: null,
                        name: product.name,
                        parentName: null,
                        orderItemId: item.id,
                        addons: addonsInfo,
                        options: optionsInfo,
                        note: customerSuppliedInfo && customerSuppliedInfo.note ? customerSuppliedInfo.note : (item.note ? item.note : null),
                        courseId: customerSuppliedInfo && customerSuppliedInfo.courseId ? customerSuppliedInfo.courseId : product.defaultCourseId,
                        locationId: product.locationId ? product.locationId : null,
                    });
                    todoItems.push(todoItem);
                }
            }
        }
        return todoItems;
    }
}
